import {
  AdexpLog,
  AdexpMetricV2,
  AdexpMessage,
  AdexpJavaScriptError,
} from "./adexp";
import {
  normalizeError,
  normalizeErrorEvent,
  shouldIgnoreError,
} from "../error_handling";
import { request } from "../frontendlib/utils";

/**
 * Sends log to Adexp.
 */
const postJSON = (url, data) => {
  /**
   * TODO(oscar): Investigate if we could improve empty payloads.
   * @see https://github.com/liftoffio/liftoff/pull/25293
   */
  if (data == null) {
    console.error("No data to post for logging.");
    return Promise.resolve();
  }
  const payload = JSON.stringify(data);

  if (payload.length < 2) {
    console.error("Data could not be stringified for logging.");
    return Promise.resolve();
  }

  return request(
    "POST",
    url,
    {
      // Don't set the content type to "application/json" because that requires
      // a preflight OPTIONS request (because this is a cross-domain request).
      "Content-Type": "text/plain",
    },
    payload
  ).catch(() => {
    console.error("Failed to post JSON for logging.");
  });
};

const postMetricV2 = (
  { adexpContextSnapshotFn, endpoint, eventTrackingParam },
  name,
  value
) => {
  const adexpContext = adexpContextSnapshotFn();
  const payload = new AdexpMessage({
    context: adexpContext,
    metric: new AdexpMetricV2(name, value),
  });

  return postJSON(`${endpoint}/adexp_metrics/${eventTrackingParam}`, payload);
};

class Logger {
  // #if process.env.NODE_ENV === "test"
//   static resetForTests() {
//     delete this.enabled;
//     delete this.loggedErrors;
//     delete this.endpoint;
//     delete this.eventTrackingParam;
//     delete this.adexpContextSnapshotFn;
//   }
  // #endif

  static init() {
    this.enabled = false;
    this.loggedErrors = [];
  }

  static enable() {
    this.enabled = true;
  }

  /**
   * Pass the logger required context
   * @param {string} endpoint - The endpoint to which the creative will post
   * AdExp logs or metrics.
   * @param {string} eventTrackingParam - The tracking parameter string which
   * includes encoded bid context information.
   * @param {function} adexpContextSnapshotFn - A function to call that will
   * take a snapshot of the current environment and return an `AdexpContext`
   * object.
   */
  static passContext(endpoint, eventTrackingParam, adexpContextSnapshotFn) {
    this.endpoint = endpoint;
    this.eventTrackingParam = eventTrackingParam;
    this.adexpContextSnapshotFn = adexpContextSnapshotFn;
  }

  /**
   * Listens for errors that are caught by the window and reports them to adexp.
   * Don't call this if you use your own custom error handlers.
   */
  static addErrorListeners() {
    window.addEventListener("error", (errorEvent) => {
      const normalizedError = normalizeErrorEvent(errorEvent);
      this.recordError(normalizedError);
    });
  }

  /**
   * recordError takes an object like:
   * { message, line, column, sourceURL ... }
   * and logs the error to adexp.
   */
  static recordError(e) {
    this.recordLabeledError("error", e);
  }

  static recordLabeledError(tag, e) {
    // #if process.env.NODE_ENV !== "test"
    console.error(e);
    // #endif
    this._processError(tag, e);
  }

  static _processError(tag, errorObj) {
    if (shouldIgnoreError(errorObj)) {
      return;
    }

    const normalizedErr = normalizeError(errorObj);

    // Only log one error per (sourceURL, line #, message) combination. This
    // prevents noise from certain errors that are logged many times
    // from a single impression.
    const errorIdentifier = [
      normalizedErr.sourceURL,
      normalizedErr.line,
      normalizedErr.message,
    ].join("-");

    if (this.loggedErrors.includes(errorIdentifier)) {
      return;
    }

    this.loggedErrors.push(errorIdentifier);
    this.recordLog(
      tag,
      normalizedErr.message,
      new AdexpJavaScriptError(normalizedErr)
    );
  }

  static recordLog(tag, message, data) {
    if (!this.enabled) {
      return;
    }

    const adexpContext = this.adexpContextSnapshotFn();
    const payload = new AdexpMessage({
      context: adexpContext,
      log: new AdexpLog(tag, message, data),
    });

    return postJSON(
      `${this.endpoint}/adexp_log/${this.eventTrackingParam}`,
      payload
    );
  }

  static recordMetricV2(name, value) {
    if (!this.enabled) {
      return Promise.resolve();
    }
    return postMetricV2(
      {
        endpoint: this.endpoint,
        eventTrackingParam: this.eventTrackingParam,
        adexpContextSnapshotFn: this.adexpContextSnapshotFn,
      },
      name,
      value
    );
  }

  static recordVXOnlyAdexpMetric(name, value) {
    if (!Number.isInteger(value)) {
      console.debug("Expected an integer value for VXOnlyAdexpMetric: " + name);
      return;
    }

    return this.alwaysRecordMetricV2("custom_event", {
      name: name,
      value: value,
    });
  }

  static alwaysRecordMetricV2(name, value) {
    return postMetricV2(
      {
        endpoint: this.endpoint,
        eventTrackingParam: this.eventTrackingParam,
        adexpContextSnapshotFn: this.adexpContextSnapshotFn,
      },
      name,
      value
    );
  }
}

// TODO(oscar): Create a global Logger.
Logger.init();

export default Logger;



// WEBPACK FOOTER //
// ./src/js/madlib/logger.js